/* Six digits, typed fast, while looking at a customer.
 *
 * A NUMERIC PAD RATHER THAN A TEXT FIELD
 * The tablet's own keyboard covers half the screen, appears and disappears
 * under the staff member's hand, and offers autocorrect on a number. A fixed
 * pad is always in the same place, which is what makes it fast on the
 * hundredth use rather than the first.
 *
 * NO ANIMATION ON A KEYPRESS BEYOND THE PRESS ITSELF.
 * This is the most-repeated interaction in the product: a busy till hits these
 * keys hundreds of times a day. Anything that plays on each press reads as lag
 * by the tenth one. The keys get `scale(0.975)` from the button primitive and
 * nothing else.
 *
 * IT SUBMITS ON THE SIXTH DIGIT
 * Because there is no ambiguity about length, and asking for a confirming tap
 * after the last digit is a tap per customer for no information. The clear key
 * is the way back.
 */
import { Button } from "@damga/ds";
import { t } from "@damga/kit";
import { useCallback, useEffect, useRef, useState } from "react";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

export function Keypad({
  onSubmit,
  busy,
  disabled,
  label,
}: {
  onSubmit: (code: string) => void;
  busy: boolean;
  disabled: boolean;
  /** What the pad is for, when it is not a stamp. */
  label?: string;
}) {
  const [code, setCode] = useState("");
  // The authoritative value lives in a ref, and here is why.
  //
  // The first version called onSubmit from INSIDE the setState updater. React
  // treats an updater as render-phase work and invokes it twice under
  // StrictMode, so a single tap on the sixth digit fired TWO earns, each
  // minting its own idempotency key. Idempotency could not collapse them
  // because they were, as far as the server could tell, two genuine attempts.
  //
  // Nothing was double-stamped when it happened, because the minimum-gap rule
  // refused the second. That is the ledger protecting the customer from the
  // interface, which is not a control to rely on: a merchant with a zero gap
  // has no such protection, and the counter would have quietly stamped twice.
  //
  // So the submit happens in the event handler, exactly once per tap.
  const codeRef = useRef("");

  const press = useCallback(
    (digit: string) => {
      if (codeRef.current.length >= 6) return;
      const next = codeRef.current + digit;
      if (next.length === 6) {
        // Cleared before the request rather than after it, so the next
        // customer is not waiting behind a pad still showing the last one's
        // digits.
        codeRef.current = "";
        setCode("");
        onSubmit(next);
        return;
      }
      codeRef.current = next;
      setCode(next);
    },
    [onSubmit],
  );

  const edit = useCallback((fn: (c: string) => string) => {
    codeRef.current = fn(codeRef.current);
    setCode(codeRef.current);
  }, []);

  // A hardware keyboard is common on a till with a stand, and staff who have
  // one will use it. Same handler, so the two paths cannot diverge.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (busy || disabled) return;
      if (/^\d$/.test(e.key)) press(e.key);
      else if (e.key === "Backspace") edit((c) => c.slice(0, -1));
      else if (e.key === "Escape") edit(() => "");
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [press, edit, busy, disabled]);

  return (
    <section className="pad">
      <p className="label">{label ?? t("counter.enterCode")}</p>

      <p className="pad-code mono" aria-live="polite" aria-label={`${code.length} of 6 digits`}>
        {/* Six slots always, so the field does not change width as it fills
            and the eye does not have to re-find it. */}
        {Array.from({ length: 6 }, (_, i) => (
          <span key={i} data-filled={i < code.length ? "" : undefined}>
            {code[i] ?? ""}
          </span>
        ))}
      </p>

      <div className="pad-keys">
        {KEYS.map((k) => (
          <Button
            key={k}
            size="counter"
            variant="secondary"
            onClick={() => press(k)}
            disabled={busy || disabled}
          >
            {k}
          </Button>
        ))}
        <Button
          size="counter"
          variant="ghost"
          onClick={() => edit((c) => c.slice(0, -1))}
          disabled={busy || disabled || code.length === 0}
          aria-label="Delete last digit"
        >
          &larr;
        </Button>
        <Button
          size="counter"
          variant="secondary"
          onClick={() => press("0")}
          disabled={busy || disabled}
        >
          0
        </Button>
        <Button
          size="counter"
          variant="ghost"
          onClick={() => edit(() => "")}
          disabled={busy || disabled || code.length === 0}
        >
          Clear
        </Button>
      </div>

      {busy && (
        <p className="muted pad-busy" role="status">
          {t("common.loading")}
        </p>
      )}
    </section>
  );
}
